import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { endpointFromAngles, lineEndpoints, lineMetrics, validLine } from '../geometry/line3d';
import { useEditorStore } from '../store/editorStore';
import type { DrawingEntity, Vec3 } from '../types';
import { NumericField } from './NumericField';

const rounded = (value: number) => Math.round(value * 1000) / 1000;

export function LineToolEditor({ drawing }: { drawing?: DrawingEntity }) {
  const { t } = useTranslation();
  const origin = useEditorStore((state) => state.project.origin);
  const addLine3D = useEditorStore((state) => state.addLine3D);
  const updateLine3D = useEditorStore((state) => state.updateLine3D);
  const selectDrawing = useEditorStore((state) => state.selectDrawing);
  const setActiveTool = useEditorStore((state) => state.setActiveTool);
  const initial = (): [Vec3, Vec3] => drawing && drawing.points.length >= 2
    ? lineEndpoints(drawing)
    : [[...origin], [origin[0] + 1000, origin[1], origin[2]]];
  const [start, setStart] = useState<Vec3>(() => [...initial()[0]]);
  const [end, setEnd] = useState<Vec3>(() => [...initial()[1]]);
  const sourceKey = drawing && drawing.points.length >= 2
    ? JSON.stringify(drawing.points3d ?? drawing.points)
    : `${origin[0]},${origin[1]},${origin[2]}`;
  useEffect(() => { const [nextStart, nextEnd] = initial(); setStart([...nextStart]); setEnd([...nextEnd]); }, [drawing?.id, sourceKey]);
  const metric = lineMetrics([start, end]);
  const changePoint = (which: 'start' | 'end', axis: 0 | 1 | 2, value: number) => {
    if (which === 'start') setStart((current) => current.map((number, index) => index === axis ? value : number) as Vec3);
    else setEnd((current) => current.map((number, index) => index === axis ? value : number) as Vec3);
  };
  const changePolar = (length: number, azimuth: number, elevation: number) => setEnd(endpointFromAngles(start, length, azimuth, elevation));
  const save = () => {
    if (!validLine([start, end])) return;
    if (drawing) updateLine3D(drawing.id, start, end);
    else addLine3D(start, end);
  };
  return <div className="line-tool-editor">
    <p className="line-tool-note">{t('line.axisHint')}</p>
    <div className="subsection-label">{t('line.start')}</div>
    <div className="axis-row">{(['X', 'Y', 'Z'] as const).map((axis, index) => <NumericField key={axis} label={axis} value={start[index]} step={.001} min={-1000000} max={1000000} onCommit={(value) => changePoint('start', index as 0 | 1 | 2, value)} />)}</div>
    <div className="subsection-label">{t('line.end')}</div>
    <div className="axis-row">{(['X', 'Y', 'Z'] as const).map((axis, index) => <NumericField key={axis} label={axis} value={end[index]} step={.001} min={-1000000} max={1000000} onCommit={(value) => changePoint('end', index as 0 | 1 | 2, value)} />)}</div>
    <div className="subsection-label">{t('line.polar')}</div>
    <NumericField label={t('line.length')} value={rounded(metric.length)} step={.001} min={0} max={2000000} onCommit={(value) => changePolar(value, metric.azimuth, metric.elevation)} />
    <div className="line-angle-grid"><NumericField label={t('line.azimuth')} value={rounded(metric.azimuth)} unit="°" step={.001} min={-360} max={360} onCommit={(value) => changePolar(metric.length, value, metric.elevation)} /><NumericField label={t('line.elevation')} value={rounded(metric.elevation)} unit="°" step={.001} min={-90} max={90} onCommit={(value) => changePolar(metric.length, metric.azimuth, value)} /></div>
    <p className="line-tool-note">{t('line.angleHint')}</p>
    <div className="line-tool-actions">{drawing && <button type="button" onClick={() => { selectDrawing(null); setActiveTool('line'); }}>{t('line.new')}</button>}<button type="button" className="primary" disabled={!validLine([start, end])} onClick={save}>{t(drawing ? 'line.save' : 'line.create')}</button></div>
  </div>;
}
